import { applyStartOfTurn } from "./economy";
import { facingTowardClosestEnemyProduction } from "./facing";
import type {
  BuildingTileKind,
  Coord,
  FacingDir,
  GameMap,
  GameState,
  Tile,
  TileKind,
  TileOwner,
  Unit,
} from "./types";
import { MAX_HP } from "./units";

export function horizontalFacingToward({
  from,
  to,
  fallback = "down",
}: {
  from: Coord;
  to: Coord;
  fallback?: FacingDir;
}): FacingDir {
  const dx = to.x - from.x;
  if (dx > 0) return "right";
  if (dx < 0) return "left";
  return fallback;
}

export function facingFromPath({
  path,
  startPos,
  fallback,
}: {
  path: Coord[];
  startPos: Coord;
  fallback: FacingDir;
}): FacingDir {
  if (path.length === 0) return fallback;

  let current = startPos;
  let facing = fallback;

  for (const next of path) {
    if (next.x === current.x && next.y === current.y) continue;
    const dx = next.x - current.x;
    if (dx > 0) facing = "right";
    else if (dx < 0) facing = "left";
    current = next;
  }

  return facing;
}

export function facingTowardAttackTarget({
  from,
  to,
  path,
  startPos,
  currentFacing,
}: {
  from: Coord;
  to: Coord;
  path?: readonly Coord[];
  startPos: Coord;
  currentFacing: FacingDir;
}): FacingDir {
  const travelFacing =
    path && path.length > 0
      ? facingFromPath({ path: [...path], startPos, fallback: currentFacing })
      : currentFacing;
  return horizontalFacingToward({ from, to, fallback: travelFacing });
}

export function buildingTile(kind: BuildingTileKind, owner: TileOwner): Tile {
  if (kind === "cannon") return { kind, owner, hp: MAX_HP };
  return { kind, owner };
}

function deepCloneTile(t: Tile): Tile {
  return { ...t };
}

function withCannonHp(tile: Tile): Tile {
  if (tile.kind !== "cannon") return deepCloneTile(tile);
  return { ...tile, hp: tile.hp ?? MAX_HP };
}

export function createInitialState(map: GameMap): GameState {
  const base: GameState = {
    width: map.width,
    height: map.height,
    ...(map.theme !== undefined ? { theme: map.theme } : {}),
    ...(map.victoryConditions !== undefined ? { victoryConditions: map.victoryConditions } : {}),
    ...(map.captureCityTarget !== undefined ? { captureCityTarget: map.captureCityTarget } : {}),
    tiles: map.tiles.map(withCannonHp),
    units: [],
    currentPlayer: "p1",
    currentDay: 1,
    status: "in_progress",
    funds: { p1: 0, p2: 0 },
    nextUnitId: 0,
    initialUnitCount: map.startingUnits.length,
  };

  const initialSupplyTruckCount = map.startingUnits.filter(
    (unit) => unit.owner === "p1" && unit.kind === "supplyTruck",
  ).length;
  if (initialSupplyTruckCount > 0) {
    base.initialSupplyTruckCount = initialSupplyTruckCount;
  }

  const units: Unit[] = map.startingUnits.map((u, i) => ({
    id: `u${i}`,
    owner: u.owner,
    kind: u.kind,
    pos: { ...u.pos },
    hp: MAX_HP,
    hasMoved: false,
    hasAttacked: false,
    facing:
      u.facing ??
      facingTowardClosestEnemyProduction({
        state: { ...base, units: [], nextUnitId: map.startingUnits.length },
        owner: u.owner,
        pos: u.pos,
        unitKind: u.kind,
      }),
  }));

  base.units = units;
  base.nextUnitId = units.length;

  const { state } = applyStartOfTurn(base);
  return state;
}

export const TILE: Record<TileKind, Tile> = {
  plain: { kind: "plain" },
  forest: { kind: "forest" },
  mountain: { kind: "mountain" },
  sea: { kind: "sea" },
  road: { kind: "road" },
  city: { kind: "city", owner: "neutral" },
  barracks: { kind: "barracks", owner: "neutral" },
  factory: { kind: "factory", owner: "neutral" },
  base: { kind: "base", owner: "neutral" },
  airport: { kind: "airport", owner: "neutral" },
  monument: { kind: "monument", owner: "neutral" },
  cannon: { kind: "cannon", owner: "p2", hp: MAX_HP },
  dropZone: { kind: "dropZone" },
};

export function plainGrid(width: number, height: number): Tile[] {
  const tiles: Tile[] = [];
  for (let i = 0; i < width * height; i++) {
    tiles.push({ ...TILE.plain });
  }
  return tiles;
}
